import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import type { ChurnRules } from '../../types';

interface StageCondition {
  entity?: string;
  feature?: string;
  metadata?: string;
  account?: string;
  field?: string;
  equals?: any;
  notEquals?: any;
  greaterThan?: number;
  lessThan?: number;
  greaterThanOrEqual?: number;
  lessThanOrEqual?: number;
  exists?: boolean;
}

interface Stage {
  name: string;
  displayName?: string;
  displayOrder: number;
  type: 'system' | 'custom';
  editable: boolean;
  description: string;
  icon?: string;
  color?: string;
  conditions: {
    all?: StageCondition[];
    any?: StageCondition[];
  } | null;
}

interface StageConfigShape {
  stages: Stage[];
  churnRules?: ChurnRules;
}

interface StageDefinitionsProps {
  stageConfig: StageConfigShape | undefined;
  onChange: (stageConfig: StageConfigShape) => void;
  entityDefinitions?: Record<string, any>;
  featureDefinitions?: Record<string, any>;
}

/** Mirrors backend DEFAULT_CHURN_RULES. */
const DEFAULT_CHURN_RULES: Required<ChurnRules> = {
  onSubscriptionEnded: true,
  inactivityDays: 90,
  inactivityRequiresNoSubscription: true,
  neverActivatedDays: 30,
};

type OperatorType = 'gt' | 'gte' | 'lt' | 'lte' | 'eq';

// Field type registry - defines what fields exist and their data types
const FIELD_REGISTRY = {
  account: {
    fields: {
      healthScore: { type: 'number' as const, label: 'Health Score' },
      daysSinceLastActivity: { type: 'number' as const, label: 'Days Since Last Activity' },
      ttfFirstEntity: { type: 'number' as const, label: 'Time to First Entity' },
      churnedAt: { type: 'timestamp' as const, label: 'Churned At' }, // timestamp = exists check
      createdAt: { type: 'timestamp' as const, label: 'Created At' },
      lastActivityAt: { type: 'timestamp' as const, label: 'Last Activity At' },
    },
  },
  metadata: {
    fields: {
      isChurned: { type: 'boolean' as const, label: 'Is Churned' },
      isActive: { type: 'boolean' as const, label: 'Is Active' },
      accountAge: { type: 'number' as const, label: 'Account Age' },
      userCount: { type: 'number' as const, label: 'User Count' },
      companyName: { type: 'string' as const, label: 'Company Name' },
      ownerEmail: { type: 'string' as const, label: 'Owner Email' },
      lastLoginAt: { type: 'timestamp' as const, label: 'Last Login At' },
      clientSince: { type: 'timestamp' as const, label: 'Client Since' },
    },
  },
  entity: {
    fields: {
      count: { type: 'number' as const, label: 'Count' },
      last7Days: { type: 'number' as const, label: 'Last 7 Days' },
      last30Days: { type: 'number' as const, label: 'Last 30 Days' },
      daysSinceLast: { type: 'number' as const, label: 'Days Since Last' },
      daysSinceFirst: { type: 'number' as const, label: 'Days Since First' },
      totalAmount: { type: 'number' as const, label: 'Total Amount' },
    },
  },
};

// Helper to get field type
const getFieldType = (parentType: 'account' | 'metadata' | 'entity', fieldName: string): 'number' | 'boolean' | 'string' | 'timestamp' => {
  if (parentType === 'entity') {
    const field = FIELD_REGISTRY.entity.fields[fieldName as keyof typeof FIELD_REGISTRY.entity.fields];
    return field?.type || 'number';
  }
  if (parentType === 'account') {
    const field = FIELD_REGISTRY.account.fields[fieldName as keyof typeof FIELD_REGISTRY.account.fields];
    return field?.type || 'number';
  }
  if (parentType === 'metadata') {
    const field = FIELD_REGISTRY.metadata.fields[fieldName as keyof typeof FIELD_REGISTRY.metadata.fields];
    return field?.type || 'number';
  }
  return 'number';
};

// Extracted as proper components so MUI Select maintains identity across re-renders
const OperatorSelect: React.FC<{
  contextType: string;
  field1: string;
  field2: string;
  value: string;
  onChange: (op: string) => void;
  getFieldType: (parentType: 'account' | 'metadata' | 'entity', fieldName: string) => 'number' | 'boolean' | 'string' | 'timestamp';
}> = ({ contextType, field1, field2, value, onChange, getFieldType: gft }) => {
  const fieldType = contextType === 'feature' ? 'boolean' :
    contextType === 'entity' ? gft('entity', field2) :
    (contextType === 'account' || contextType === 'metadata') ? gft(contextType, field1) : 'number';
  const isBoolean = fieldType === 'boolean';
  const isTimestamp = fieldType === 'timestamp';

  return (
    <FormControl size="small" sx={{ minWidth: 50 }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        sx={{
          bgcolor: '#c4c4f7',
          color: '#1B0826',
          fontWeight: 500,
          fontSize: '13px',
          height: '26px',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': { py: 0.5, px: 1 },
          borderRadius: '16px',
        }}
      >
        <MenuItem value=""><em>op</em></MenuItem>
        {isBoolean ? [
          <MenuItem key="eq" value="eq">=</MenuItem>,
          <MenuItem key="neq" value="neq">!=</MenuItem>,
        ] : isTimestamp ? [
          <MenuItem key="eq" value="eq">=</MenuItem>,
          <MenuItem key="exists" value="exists">exists</MenuItem>,
        ] : [
          <MenuItem key="gt" value="gt">&gt;</MenuItem>,
          <MenuItem key="gte" value="gte">&gt;=</MenuItem>,
          <MenuItem key="lt" value="lt">&lt;</MenuItem>,
          <MenuItem key="lte" value="lte">&lt;=</MenuItem>,
          <MenuItem key="eq" value="eq">=</MenuItem>,
          <MenuItem key="neq" value="neq">!=</MenuItem>,
        ]}
      </Select>
    </FormControl>
  );
};

const ValueInput: React.FC<{
  contextType: string;
  field1: string;
  field2: string;
  operator: string;
  value: string;
  onChange: (val: string) => void;
  getFieldType: (parentType: 'account' | 'metadata' | 'entity', fieldName: string) => 'number' | 'boolean' | 'string' | 'timestamp';
}> = ({ contextType, field1, field2, operator, value, onChange, getFieldType: gft }) => {
  const fieldType = contextType === 'feature' ? 'boolean' :
    contextType === 'entity' ? gft('entity', field2) :
    (contextType === 'account' || contextType === 'metadata') ? gft(contextType, field1) : 'number';
  const isBoolean = fieldType === 'boolean' || operator === 'exists';

  if (isBoolean) {
    return (
      <FormControl size="small" sx={{ minWidth: 60 }}>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          sx={{
            bgcolor: '#ffd9a8',
            color: '#1B0826',
            fontWeight: 500,
            fontSize: '13px',
            height: '26px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-select': { py: 0.5, px: 1 },
            borderRadius: '16px',
          }}
        >
          <MenuItem value=""><em>value</em></MenuItem>
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      size="small"
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value"
      sx={{
        width: '70px',
        '& .MuiOutlinedInput-root': {
          bgcolor: '#ffd9a8',
          color: '#1B0826',
          fontWeight: 500,
          fontSize: '13px',
          height: '26px',
          borderRadius: '16px',
          '& fieldset': { border: 'none' },
          '& input': { py: 0.5, px: 1 },
        },
      }}
    />
  );
};

export const StageDefinitions: React.FC<StageDefinitionsProps> = ({ stageConfig, onChange, entityDefinitions = {}, featureDefinitions = {} }) => {
  const entityNames = Object.keys(entityDefinitions);
  const featureNames = Object.keys(featureDefinitions);
  const [stages, setStages] = useState<Stage[]>([]);
  const [addingBetween, setAddingBetween] = useState<number | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);
  const [newStage, setNewStage] = useState<Partial<Stage> | null>(null);
  const [addingCondition, setAddingCondition] = useState<{ stageName: string } | null>(null);
  const [newConditionData, setNewConditionData] = useState<{
    contextType: 'account' | 'metadata' | 'entity' | 'feature' | '';
    field1: string; // entity name OR feature name OR account/metadata field
    field2: string; // entity field (e.g., count, last7Days)
    operator: string;
    value: string;
  }>({ contextType: '', field1: '', field2: '', operator: '', value: '' });
  useEffect(() => {
    if (stageConfig?.stages) {
      // Sort stages for display: signup first, then custom, then at_risk, then churned
      const sorted = [...stageConfig.stages].sort((a, b) => {
        if (a.name === 'signup') return -1;
        if (b.name === 'signup') return 1;
        if (a.name === 'churned') return 1;
        if (b.name === 'churned') return -1;
        if (a.name === 'at_risk') return 1;
        if (b.name === 'at_risk') return -1;
        return a.displayOrder - b.displayOrder;
      });
      setStages(sorted);
    }
  }, [stageConfig]);

  const handleAutoSave = (updatedStages: Stage[]) => {
    // Preserve sibling keys (churnRules) — only the stage list changed.
    onChange({ ...(stageConfig || {}), stages: updatedStages });
  };

  const churnRules: Required<ChurnRules> = { ...DEFAULT_CHURN_RULES, ...(stageConfig?.churnRules || {}) };
  const updateChurnRules = (patch: Partial<ChurnRules>) => {
    onChange({ ...(stageConfig || {}), stages: stageConfig?.stages || stages, churnRules: { ...churnRules, ...patch } });
  };
  const daysField = (key: 'inactivityDays' | 'neverActivatedDays') => ({
    value: churnRules[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = parseInt(e.target.value, 10);
      updateChurnRules({ [key]: Number.isFinite(n) && n >= 0 ? Math.min(n, 3650) : 0 });
    },
  });

  const handleAddStageBetween = (afterIndex: number) => {
    setAddingBetween(afterIndex);
    setNewStage({
      name: '',
      displayName: '',
      displayOrder: 50, // Will be adjusted
      type: 'custom',
      editable: true,
      description: '',
      icon: '',
      color: '#4caf50',
      conditions: { all: [] },
    });
  };

  const handleSaveNewStage = () => {
    if (!newStage || !newStage.name) return;

    // Names are the stage identity (React keys, GSI orgStage values, history entries):
    // normalise and refuse duplicates.
    const name = newStage.name.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    if (!name) {
      setStageError('Stage name must contain letters or digits');
      return;
    }
    if (stages.some((s) => s.name === name)) {
      setStageError(`A stage named "${name}" already exists`);
      return;
    }
    setStageError(null);
    newStage.name = name;
    if (!newStage.displayName) newStage.displayName = newStage.name;

    const updatedStages = [...stages];

    // Insert after the specified index
    if (addingBetween !== null) {
      updatedStages.splice(addingBetween + 1, 0, newStage as Stage);
    }

    // Re-normalize displayOrder based on array position so the sort
    // in useEffect preserves this exact order
    const normalized = updatedStages.map((s, i) => ({ ...s, displayOrder: (i + 1) * 10 }));

    setStages(normalized);
    handleAutoSave(normalized);
    setAddingBetween(null);
    setNewStage(null);
  };

  const handleCancelNewStage = () => {
    setAddingBetween(null);
    setNewStage(null);
  };

  const handleDeleteStage = (stageName: string) => {
    const updatedStages = stages.filter(s => s.name !== stageName);
    setStages(updatedStages);
    handleAutoSave(updatedStages);
  };

  const handleMatchLogicChange = (stageName: string, logic: 'all' | 'any') => {
    const updatedStages = stages.map(s => {
      if (s.name === stageName && s.conditions) {
        const conditions = s.conditions.all || s.conditions.any || [];
        return {
          ...s,
          conditions: { [logic]: conditions },
        };
      }
      return s;
    });
    setStages(updatedStages);
    handleAutoSave(updatedStages);
  };

  const handleAddConditionClick = (stageName: string) => {
    setAddingCondition({ stageName });
    setNewConditionData({ contextType: '', field1: '', field2: '', operator: '', value: '' });
  };

  const handleDeleteCondition = (stageName: string, conditionIndex: number) => {
    const updatedStages = stages.map(s => {
      if (s.name === stageName && s.conditions) {
        const logic = s.conditions.all ? 'all' : 'any';
        const conditions = s.conditions[logic] || [];
        const filtered = conditions.filter((_, i) => i !== conditionIndex);
        return {
          ...s,
          conditions: { [logic]: filtered },
        };
      }
      return s;
    });
    setStages(updatedStages);
    handleAutoSave(updatedStages);
  };

  const handleSaveNewCondition = () => {
    if (!addingCondition || !newConditionData.contextType) return;

    let condition: StageCondition;

    // Build condition based on context type
    if (newConditionData.contextType === 'feature') {
      // "feature.X = true" means the feature is currently ENABLED, not merely
      // that the key exists (a disconnected integration keeps its key).
      condition = {
        feature: newConditionData.field1,
        field: 'enabled',
        equals: newConditionData.value === 'true',
      };
    } else if (newConditionData.contextType === 'entity') {
      const base: StageCondition = { entity: newConditionData.field1, field: newConditionData.field2 };
      condition = applyOperator(base, newConditionData.operator as OperatorType | 'neq', parseFloat(newConditionData.value));
    } else if (newConditionData.contextType === 'account') {
      const fieldType = getFieldType('account', newConditionData.field1);
      if (fieldType === 'timestamp') {
        condition = { account: newConditionData.field1, exists: newConditionData.value === 'true' };
      } else {
        const base: StageCondition = { account: newConditionData.field1 };
        condition = applyOperator(base, newConditionData.operator as OperatorType | 'neq', parseFloat(newConditionData.value));
      }
    } else if (newConditionData.contextType === 'metadata') {
      const fieldType = getFieldType('metadata', newConditionData.field1);
      if (fieldType === 'boolean') {
        if (newConditionData.operator === 'neq') {
          condition = { metadata: newConditionData.field1, notEquals: newConditionData.value === 'true' };
        } else {
          condition = { metadata: newConditionData.field1, equals: newConditionData.value === 'true' };
        }
      } else if (fieldType === 'timestamp') {
        condition = { metadata: newConditionData.field1, exists: newConditionData.value === 'true' };
      } else {
        const base: StageCondition = { metadata: newConditionData.field1 };
        condition = applyOperator(base, newConditionData.operator as OperatorType | 'neq', parseFloat(newConditionData.value));
      }
    } else {
      return;
    }

    const updatedStages = stages.map(s => {
      if (s.name === addingCondition.stageName) {
        const logic = s.conditions?.all ? 'all' : 'any';
        const existing = s.conditions?.[logic] || [];
        return {
          ...s,
          conditions: { [logic]: [...existing, condition] },
        };
      }
      return s;
    });

    setStages(updatedStages);
    handleAutoSave(updatedStages);
    setAddingCondition(null);
    setNewConditionData({ contextType: '', field1: '', field2: '', operator: '', value: '' });
  };


  const detectConditionType = (condition: StageCondition): 'boolean' | 'value' | 'unknown' => {
    if (condition.feature) return 'boolean';
    if (condition.entity && condition.field) return 'value';
    if (condition.account) return 'value';
    if (condition.metadata) return 'value'; // Metadata can be boolean or value
    return 'unknown';
  };

  const formatConditionForDisplay = (condition: StageCondition) => {
    const type = detectConditionType(condition);

    if (type === 'boolean') {
      // Boolean type: feature.stockSynced = true
      const boolValue = condition.exists !== undefined ? condition.exists : (condition.equals === true);
      return {
        conditionType: 'boolean' as const,
        parts: [
          { text: 'feature', type: 'label' as const, editable: false },
          { text: '.', type: 'dot' as const, editable: false },
          { text: condition.feature || '', type: 'featureName' as const, editable: true },
          { text: '=', type: 'operator' as const, editable: false },
          { text: String(boolValue), type: 'boolValue' as const, editable: true },
        ],
      };
    }

    if (type === 'value') {
      // Value type: entity.field operator value
      if (condition.entity && condition.field) {
        const operator = condition.greaterThan !== undefined ? '>' : condition.greaterThanOrEqual !== undefined ? '>=' : condition.lessThan !== undefined ? '<' : condition.lessThanOrEqual !== undefined ? '<=' : condition.notEquals !== undefined ? '!=' : '=';
        const value = condition.greaterThan ?? condition.greaterThanOrEqual ?? condition.lessThan ?? condition.lessThanOrEqual ?? condition.equals ?? condition.notEquals ?? 0;
        return {
          conditionType: 'value' as const,
          parts: [
            { text: condition.entity, type: 'entityName' as const, editable: true },
            { text: '.', type: 'dot' as const, editable: false },
            { text: condition.field, type: 'fieldName' as const, editable: true },
            { text: operator, type: 'operator' as const, editable: true },
            { text: String(value), type: 'numValue' as const, editable: true },
          ],
        };
      }
      if (condition.account) {
        const fieldType = getFieldType('account', condition.account);

        // Timestamp fields use exists operator
        if (fieldType === 'timestamp' && condition.exists !== undefined) {
          return {
            conditionType: 'boolean' as const,
            parts: [
              { text: 'account', type: 'label' as const, editable: false },
              { text: '.', type: 'dot' as const, editable: false },
              { text: condition.account, type: 'fieldName' as const, editable: true },
              { text: 'exists', type: 'operator' as const, editable: false },
              { text: String(condition.exists), type: 'boolValue' as const, editable: true },
            ],
          };
        }

        const operator = condition.greaterThan !== undefined ? '>' : condition.greaterThanOrEqual !== undefined ? '>=' : condition.lessThan !== undefined ? '<' : condition.lessThanOrEqual !== undefined ? '<=' : condition.notEquals !== undefined ? '!=' : '=';
        const value = condition.greaterThan ?? condition.greaterThanOrEqual ?? condition.lessThan ?? condition.lessThanOrEqual ?? condition.equals ?? condition.notEquals ?? 0;
        return {
          conditionType: 'value' as const,
          parts: [
            { text: 'account', type: 'label' as const, editable: false },
            { text: '.', type: 'dot' as const, editable: false },
            { text: condition.account, type: 'fieldName' as const, editable: true },
            { text: operator, type: 'operator' as const, editable: true },
            { text: String(value), type: 'numValue' as const, editable: true },
          ],
        };
      }
      if (condition.metadata) {
        const fieldType = getFieldType('metadata', condition.metadata);

        // Boolean metadata fields - check equals OR notEquals
        if (fieldType === 'boolean') {
          if (condition.equals !== undefined) {
            return {
              conditionType: 'boolean' as const,
              parts: [
                { text: 'metadata', type: 'label' as const, editable: false },
                { text: '.', type: 'dot' as const, editable: false },
                { text: condition.metadata, type: 'fieldName' as const, editable: true },
                { text: '=', type: 'operator' as const, editable: false },
                { text: String(condition.equals), type: 'boolValue' as const, editable: true },
              ],
            };
          }
          if (condition.notEquals !== undefined) {
            return {
              conditionType: 'boolean' as const,
              parts: [
                { text: 'metadata', type: 'label' as const, editable: false },
                { text: '.', type: 'dot' as const, editable: false },
                { text: condition.metadata, type: 'fieldName' as const, editable: true },
                { text: '!=', type: 'operator' as const, editable: true },
                { text: String(condition.notEquals), type: 'boolValue' as const, editable: true },
              ],
            };
          }
        }

        // Timestamp metadata fields
        if (fieldType === 'timestamp' && condition.exists !== undefined) {
          return {
            conditionType: 'boolean' as const,
            parts: [
              { text: 'metadata', type: 'label' as const, editable: false },
              { text: '.', type: 'dot' as const, editable: false },
              { text: condition.metadata, type: 'fieldName' as const, editable: true },
              { text: 'exists', type: 'operator' as const, editable: false },
              { text: String(condition.exists), type: 'boolValue' as const, editable: true },
            ],
          };
        }

        // Numeric/string metadata
        const operator = condition.greaterThan !== undefined ? '>' : condition.greaterThanOrEqual !== undefined ? '>=' : condition.lessThan !== undefined ? '<' : condition.lessThanOrEqual !== undefined ? '<=' : condition.notEquals !== undefined ? '!=' : '=';
        const value = condition.greaterThan ?? condition.greaterThanOrEqual ?? condition.lessThan ?? condition.lessThanOrEqual ?? condition.equals ?? condition.notEquals ?? 0;
        return {
          conditionType: 'value' as const,
          parts: [
            { text: 'metadata', type: 'label' as const, editable: false },
            { text: '.', type: 'dot' as const, editable: false },
            { text: condition.metadata, type: 'fieldName' as const, editable: true },
            { text: operator, type: 'operator' as const, editable: true },
            { text: String(value), type: 'numValue' as const, editable: true },
          ],
        };
      }
    }

    return {
      conditionType: 'unknown' as const,
      parts: [{ text: JSON.stringify(condition), type: 'error' as const, editable: false }],
    };
  };

  const parseConditionForEdit = (condition: StageCondition) => {
    if (condition.feature) {
      // Prefer the enabled comparison; fall back to legacy `exists` conditions.
      const value = condition.equals !== undefined
        ? (condition.equals === true || condition.equals === 'true' ? 'true' : 'false')
        : (condition.exists ? 'true' : 'false');
      return {
        type: 'feature' as const,
        field1: condition.feature,
        operator: 'exists' as const,
        value: value,
      };
    }
    if (condition.account) {
      const fieldType = getFieldType('account', condition.account);

      // Timestamp fields use exists
      if (fieldType === 'timestamp' && condition.exists !== undefined) {
        return {
          type: 'account_timestamp' as const,
          field1: condition.account,
          operator: 'exists' as const,
          value: String(condition.exists),
        };
      }

      const operator = condition.greaterThan !== undefined ? 'gt' : condition.greaterThanOrEqual !== undefined ? 'gte' : condition.lessThan !== undefined ? 'lt' : condition.lessThanOrEqual !== undefined ? 'lte' : condition.notEquals !== undefined ? 'neq' : 'eq';
      const value = condition.greaterThan ?? condition.greaterThanOrEqual ?? condition.lessThan ?? condition.lessThanOrEqual ?? condition.equals ?? condition.notEquals;
      return {
        type: 'account' as const,
        field1: condition.account,
        operator: operator as OperatorType | 'neq',
        value: String(value),
      };
    }
    if (condition.metadata) {
      const fieldType = getFieldType('metadata', condition.metadata);

      // Boolean fields - handle both equals and notEquals
      if (fieldType === 'boolean') {
        if (condition.equals !== undefined) {
          return {
            type: 'metadata_boolean' as const,
            field1: condition.metadata,
            operator: 'eq' as const,
            value: String(condition.equals),
          };
        }
        if (condition.notEquals !== undefined) {
          return {
            type: 'metadata_boolean_not' as const,
            field1: condition.metadata,
            operator: 'neq' as const,
            value: String(condition.notEquals),
          };
        }
      }

      // Timestamp fields
      if (fieldType === 'timestamp' && condition.exists !== undefined) {
        return {
          type: 'metadata_timestamp' as const,
          field1: condition.metadata,
          operator: 'exists' as const,
          value: String(condition.exists),
        };
      }

      const operator = condition.greaterThan !== undefined ? 'gt' : condition.greaterThanOrEqual !== undefined ? 'gte' : condition.lessThan !== undefined ? 'lt' : condition.lessThanOrEqual !== undefined ? 'lte' : condition.notEquals !== undefined ? 'neq' : 'eq';
      const value = condition.greaterThan ?? condition.greaterThanOrEqual ?? condition.lessThan ?? condition.lessThanOrEqual ?? condition.equals ?? condition.notEquals;
      return {
        type: 'metadata' as const,
        field1: condition.metadata,
        operator: operator as OperatorType | 'neq',
        value: String(value),
      };
    }
    if (condition.entity && condition.field) {
      const operator = condition.greaterThan !== undefined ? 'gt' : condition.greaterThanOrEqual !== undefined ? 'gte' : condition.lessThan !== undefined ? 'lt' : condition.lessThanOrEqual !== undefined ? 'lte' : condition.notEquals !== undefined ? 'neq' : 'eq';
      const value = condition.greaterThan ?? condition.greaterThanOrEqual ?? condition.lessThan ?? condition.lessThanOrEqual ?? condition.equals ?? condition.notEquals;
      return {
        type: 'entity' as const,
        field1: condition.entity,
        field2: condition.field,
        operator: operator as OperatorType | 'neq',
        value: String(value),
      };
    }
    return null;
  };

  const handleUpdateCondition = (stageName: string, conditionIndex: number, updates: { field1?: string; field2?: string; operator?: string; value?: string }) => {
    const updatedStages = stages.map(s => {
      if (s.name === stageName && s.conditions) {
        const logic = s.conditions.all ? 'all' : 'any';
        const conditions = [...(s.conditions[logic] || [])];
        const oldCondition = conditions[conditionIndex];
        const parsed = parseConditionForEdit(oldCondition);

        if (!parsed) return s;

        const updated = { ...parsed, ...updates };
        let newCondition: StageCondition;

        if (updated.type === 'feature') {
          newCondition = { feature: updated.field1, field: 'enabled', equals: updated.value === 'true' };
        } else if (updated.type === 'metadata_boolean') {
          newCondition = { metadata: updated.field1, equals: updated.value === 'true' };
        } else if (updated.type === 'metadata_boolean_not') {
          newCondition = { metadata: updated.field1, notEquals: updated.value === 'true' };
        } else if (updated.type === 'metadata_timestamp') {
          newCondition = { metadata: updated.field1, exists: updated.value === 'true' };
        } else if (updated.type === 'account_timestamp') {
          newCondition = { account: updated.field1, exists: updated.value === 'true' };
        } else if (updated.type === 'account') {
          const base = { account: updated.field1 };
          newCondition = applyOperator(base, updated.operator as OperatorType | 'neq', parseFloat(updated.value));
        } else if (updated.type === 'metadata') {
          const base = { metadata: updated.field1 };
          newCondition = applyOperator(base, updated.operator as OperatorType | 'neq', parseFloat(updated.value));
        } else {
          const base = { entity: updated.field1, field: updated.field2 };
          newCondition = applyOperator(base, updated.operator as OperatorType | 'neq', parseFloat(updated.value));
        }

        conditions[conditionIndex] = newCondition;
        return { ...s, conditions: { [logic]: conditions } };
      }
      return s;
    });

    setStages(updatedStages);
    handleAutoSave(updatedStages);
  };

  const applyOperator = (base: StageCondition, operator: OperatorType | "neq", value: number): StageCondition => {
    // Never emit NaN/null thresholds (an empty numeric input); keep the condition comparable.
    if (!Number.isFinite(value)) return { ...base, greaterThanOrEqual: 0 };
    switch (operator) {
      case 'gt': return { ...base, greaterThan: value };
      case 'gte': return { ...base, greaterThanOrEqual: value };
      case 'lt': return { ...base, lessThan: value };
      case 'lte': return { ...base, lessThanOrEqual: value };
      case 'eq': return { ...base, equals: value };
      case 'neq': return { ...base, notEquals: value };
      default: return base;
    }
  };

  const canAddStageBetween = (index: number): boolean => {
    const stage = stages[index];
    const nextStage = stages[index + 1];
    // Can only add between signup and at_risk (not after at_risk or churned)
    return stage.name === 'signup' || (stage.type === 'custom' && nextStage?.name !== 'churned');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Journey Stage Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Define customer lifecycle stages and progression rules
          </Typography>
        </Box>
      </Box>

      {stageError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStageError(null)}>
          {stageError}
        </Alert>
      )}

      {/* Built-in churn rules: evaluated before any stage condition */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Churn rules</Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Checked first, on every event and every hour. A churned account stops counting toward your plan 60 days after it churns, and comes back automatically if it becomes active again.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={3} alignItems="center">
          <FormControlLabel
            control={<Switch checked={churnRules.onSubscriptionEnded} onChange={(e) => updateChurnRules({ onSubscriptionEnded: e.target.checked })} />}
            label="Churn when the subscription is cancelled or expires"
          />
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Churn after</Typography>
            <TextField size="small" type="number" sx={{ width: 90 }} inputProps={{ min: 0, max: 3650 }} {...daysField('inactivityDays')} />
            <Typography variant="body2">days without activity (0 = never)</Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={churnRules.inactivityRequiresNoSubscription} onChange={(e) => updateChurnRules({ inactivityRequiresNoSubscription: e.target.checked })} />}
            label="…only if there is no active subscription"
          />
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Never activated (no data, no subscription) after</Typography>
            <TextField size="small" type="number" sx={{ width: 90 }} inputProps={{ min: 0, max: 3650 }} {...daysField('neverActivatedDays')} />
            <Typography variant="body2">days (0 = never)</Typography>
          </Box>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>User Flow:</strong> SIGNUP → Custom Stages → AT RISK → CHURNED.
        Accounts progress left to right. Add custom stages between SIGNUP and AT RISK only.
      </Alert>

      {/* Horizontal scrollable stage flow */}
      <Box sx={{ overflowX: 'auto', pb: 2 }}>
        <Box display="flex" gap={0} alignItems="center" minWidth="fit-content">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.name}>
              {/* Stage Card */}
              <Paper
                elevation={0}
                sx={{
                  minWidth: 400,
                  maxWidth: 800,
                  p: 2,
                  border: stage.color ? `2px solid ${stage.color}` : '2px solid #e0e0e0',
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={600}>
                      {stage.displayName || stage.name.toUpperCase()}
                    </Typography>
                    {stage.type === 'system' && (
                      <Chip label="System Stage" size="small" color="primary" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                  {stage.type === 'custom' && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteStage(stage.name)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Color picker */}
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="caption" color="text.secondary">Color:</Typography>
                  <Box
                    component="label"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: stage.color || '#1976d2',
                      border: '2px solid',
                      borderColor: stage.color || '#1976d2',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    <input
                      type="color"
                      value={stage.color || '#1976d2'}
                      onChange={(e) => {
                        const updatedStages = stages.map(s =>
                          s.name === stage.name ? { ...s, color: e.target.value } : s
                        );
                        setStages(updatedStages);
                        handleAutoSave(updatedStages);
                      }}
                      style={{
                        opacity: 0,
                        position: 'absolute',
                        width: 0,
                        height: 0,
                        border: 'none',
                        padding: 0,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {stage.color || '#1976d2'}
                  </Typography>
                </Box>

                {/* Description */}
                {stage.description && (
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {stage.description}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Match Logic */}
                {stage.conditions && stage.name !== 'signup' && (
                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Match Logic:
                    </Typography>
                    <RadioGroup
                      row
                      value={stage.conditions.all ? 'all' : 'any'}
                      onChange={(e) => handleMatchLogicChange(stage.name, e.target.value as 'all' | 'any')}
                    >
                      <FormControlLabel
                        value="all"
                        control={<Radio size="small" sx={{ color: '#1B0826', '&.Mui-checked': { color: '#1B0826' } }} />}
                        label="ALL"
                      />
                      <FormControlLabel
                        value="any"
                        control={<Radio size="small" sx={{ color: '#1B0826', '&.Mui-checked': { color: '#1B0826' } }} />}
                        label="ANY"
                      />
                    </RadioGroup>
                  </Box>
                )}

                {/* Conditions */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1} fontWeight={600}>
                    Conditions:
                  </Typography>

                  {stage.name === 'signup' ? (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      New accounts start here automatically. No conditions.
                    </Typography>
                  ) : (
                    <>
                      {/* Existing conditions - Chip-based display with inline editing */}
                      <Box display="flex" flexDirection="column" gap={1} mb={1}>
                        {((stage.conditions?.all || stage.conditions?.any || []) as StageCondition[]).map((condition, idx) => {
                          const formatted = formatConditionForDisplay(condition);
                          // Positional key: keying on the condition's content would remount the
                          // row (and drop input focus) on every keystroke.
                          const conditionKey = `${stage.name}-${idx}`;

                          return (
                            <Paper
                              key={conditionKey}
                              variant="outlined"
                              sx={{
                                p: 1,
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                width: 'fit-content',
                                minWidth: '100%',
                              }}
                            >
                              {formatted.parts.map((part, partIdx) => {
                                // Color scheme
                                let chipColor = '#e8e8e8';
                                let textColor = '#1B0826';

                                if (part.type === 'label') {
                                  chipColor = '#c4c4f7'; // Purple for non-editable labels
                                } else if (part.type === 'entityName' || part.type === 'featureName') {
                                  chipColor = '#b8e6d5'; // Green for entity/feature names
                                } else if (part.type === 'fieldName') {
                                  chipColor = '#b8e6d5'; // Green for field names
                                } else if (part.type === 'operator') {
                                  chipColor = '#c4c4f7'; // Purple for operators
                                } else if (part.type === 'numValue' || part.type === 'boolValue') {
                                  chipColor = '#ffd9a8'; // Orange for values
                                }

                                if (part.type === 'dot') {
                                  return (
                                    <Typography key={partIdx} variant="body2" sx={{ color: '#999', mx: -0.3 }}>
                                      {part.text}
                                    </Typography>
                                  );
                                }

                                // Editable chip with dropdown
                                if (part.editable) {
                                  if (part.type === 'operator') {
                                    return (
                                      <Select
                                        key={`${stage.name}-${idx}-op-${part.text}`}
                                        value={part.text}
                                        onChange={(e) => {
                                          const operatorMap: Record<string, OperatorType | 'neq'> = { '>': 'gt', '>=': 'gte', '<': 'lt', '<=': 'lte', '=': 'eq', '!=': 'neq' };
                                          handleUpdateCondition(stage.name, idx, { operator: operatorMap[e.target.value] });
                                        }}
                                        size="small"
                                        sx={{
                                          bgcolor: chipColor,
                                          color: textColor,
                                          fontWeight: 500,
                                          fontSize: '13px',
                                          height: '26px',
                                          minWidth: 50,
                                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                          '& .MuiSelect-select': { py: 0.5, px: 1 },
                                          borderRadius: '16px',
                                        }}
                                      >
                                        <MenuItem value=">">&gt;</MenuItem>
                                        <MenuItem value=">=">&gt;=</MenuItem>
                                        <MenuItem value="<">&lt;</MenuItem>
                                        <MenuItem value="<=">&lt;=</MenuItem>
                                        <MenuItem value="=">=</MenuItem>
                                        <MenuItem value="!=">!=</MenuItem>
                                        <MenuItem value="exists">exists</MenuItem>
                                      </Select>
                                    );
                                  }

                                  if (part.type === 'boolValue') {
                                    return (
                                      <FormControl key={partIdx} size="small" sx={{ minWidth: 60 }}>
                                        <Select
                                          value={part.text}
                                          onChange={(e) => handleUpdateCondition(stage.name, idx, { value: e.target.value })}
                                          sx={{
                                            bgcolor: chipColor,
                                            color: textColor,
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            height: '26px',
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                                            borderRadius: '16px',
                                          }}
                                        >
                                          <MenuItem value="true">true</MenuItem>
                                          <MenuItem value="false">false</MenuItem>
                                        </Select>
                                      </FormControl>
                                    );
                                  }

                                  if (part.type === 'fieldName') {
                                    // Determine which fields to show based on parent type
                                    const parentType = formatted.parts[0]?.text; // 'account', 'metadata', or entity name
                                    let fieldOptions: string[] = [];

                                    if (parentType === 'account') {
                                      fieldOptions = Object.keys(FIELD_REGISTRY.account.fields);
                                    } else if (parentType === 'metadata') {
                                      fieldOptions = Object.keys(FIELD_REGISTRY.metadata.fields);
                                    } else {
                                      // Entity fields
                                      fieldOptions = Object.keys(FIELD_REGISTRY.entity.fields);
                                    }

                                    return (
                                      <FormControl key={partIdx} size="small" sx={{ minWidth: 120 }}>
                                        <Select
                                          value={part.text}
                                          onChange={(e) => handleUpdateCondition(stage.name, idx, { field2: e.target.value })}
                                          sx={{
                                            bgcolor: chipColor,
                                            color: textColor,
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            height: '26px',
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                                            borderRadius: '16px',
                                          }}
                                        >
                                          {fieldOptions.map((field) => (
                                            <MenuItem key={field} value={field}>{field}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    );
                                  }

                                  // Dropdown for entity/feature names, text input for numeric values
                                  if (part.type === 'entityName') {
                                    return (
                                      <FormControl key={partIdx} size="small" sx={{ minWidth: 100 }}>
                                        <Select
                                          value={part.text}
                                          onChange={(e) => handleUpdateCondition(stage.name, idx, { field1: e.target.value })}
                                          sx={{
                                            bgcolor: chipColor,
                                            color: textColor,
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            height: '26px',
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                                            borderRadius: '16px',
                                          }}
                                        >
                                          {entityNames.map((name) => (
                                            <MenuItem key={name} value={name}>{name}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    );
                                  }

                                  if (part.type === 'featureName') {
                                    return (
                                      <FormControl key={partIdx} size="small" sx={{ minWidth: 120 }}>
                                        <Select
                                          value={part.text}
                                          onChange={(e) => handleUpdateCondition(stage.name, idx, { field1: e.target.value })}
                                          sx={{
                                            bgcolor: chipColor,
                                            color: textColor,
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            height: '26px',
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                                            borderRadius: '16px',
                                          }}
                                        >
                                          {featureNames.map((name) => (
                                            <MenuItem key={name} value={name}>{name}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    );
                                  }

                                  // Text input for numeric values only
                                  return (
                                    <TextField
                                      key={partIdx}
                                      value={part.text}
                                      onChange={(e) => {
                                        if (part.type === 'numValue') {
                                          handleUpdateCondition(stage.name, idx, { value: e.target.value });
                                        }
                                      }}
                                      size="small"
                                      type="number"
                                      sx={{
                                        width: '70px',
                                        '& .MuiOutlinedInput-root': {
                                          bgcolor: chipColor,
                                          color: textColor,
                                          fontWeight: 500,
                                          fontSize: '13px',
                                          height: '26px',
                                          borderRadius: '16px',
                                          '& fieldset': { border: 'none' },
                                          '& input': { py: 0.5, px: 1 },
                                        },
                                      }}
                                    />
                                  );
                                }

                                // Non-editable chip
                                return (
                                  <Chip
                                    key={partIdx}
                                    label={part.text}
                                    size="small"
                                    sx={{
                                      bgcolor: chipColor,
                                      color: textColor,
                                      fontWeight: 500,
                                      fontSize: '13px',
                                      height: '26px',
                                    }}
                                  />
                                );
                              })}

                              {/* Delete button only */}
                              <Box display="flex" gap={0.5} ml="auto">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteCondition(stage.name, idx)}
                                  sx={{ width: 24, height: 24 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>

                      {/* Add condition inline builder - chip-based */}
                      {addingCondition?.stageName === stage.name ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1,
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            width: 'fit-content',
                            minWidth: '100%',
                          }}
                        >
                          {/* Context type selector (account, metadata, entity, feature) */}
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={newConditionData.contextType}
                              onChange={(e) => setNewConditionData({ ...newConditionData, contextType: e.target.value as any, field1: '', field2: '', operator: '', value: '' })}
                              displayEmpty
                              sx={{
                                bgcolor: '#c4c4f7',
                                color: '#1B0826',
                                fontWeight: 500,
                                fontSize: '13px',
                                height: '26px',
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                '& .MuiSelect-select': { py: 0.5, px: 1 },
                                borderRadius: '16px',
                              }}
                            >
                              <MenuItem value=""><em>select type</em></MenuItem>
                              <MenuItem value="account">account</MenuItem>
                              <MenuItem value="metadata">metadata</MenuItem>
                              <MenuItem value="entity">entity</MenuItem>
                              <MenuItem value="feature">feature</MenuItem>
                            </Select>
                          </FormControl>

                          {/* Dot separator */}
                          {newConditionData.contextType && newConditionData.contextType !== 'feature' && (
                            <Typography variant="body2" sx={{ color: '#999', mx: -0.3 }}>.</Typography>
                          )}

                          {/* Field1: entity name, feature name, or account/metadata field */}
                          {newConditionData.contextType === 'entity' && (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={newConditionData.field1}
                                onChange={(e) => setNewConditionData({ ...newConditionData, field1: e.target.value, field2: '', operator: '', value: '' })}
                                displayEmpty
                                sx={{
                                  bgcolor: '#b8e6d5',
                                  color: '#1B0826',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  height: '26px',
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                                  borderRadius: '16px',
                                }}
                              >
                                <MenuItem value=""><em>entity</em></MenuItem>
                                {entityNames.map((name) => (
                                  <MenuItem key={name} value={name}>{name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {newConditionData.contextType === 'feature' && (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={newConditionData.field1}
                                onChange={(e) => setNewConditionData({ ...newConditionData, field1: e.target.value, operator: 'eq', value: '' })}
                                displayEmpty
                                sx={{
                                  bgcolor: '#b8e6d5',
                                  color: '#1B0826',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  height: '26px',
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                                  borderRadius: '16px',
                                }}
                              >
                                <MenuItem value=""><em>feature</em></MenuItem>
                                {featureNames.map((name) => (
                                  <MenuItem key={name} value={name}>{name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {(newConditionData.contextType === 'account' || newConditionData.contextType === 'metadata') && (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={newConditionData.field1}
                                onChange={(e) => setNewConditionData({ ...newConditionData, field1: e.target.value, operator: '', value: '' })}
                                displayEmpty
                                sx={{
                                  bgcolor: '#b8e6d5',
                                  color: '#1B0826',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  height: '26px',
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                                  borderRadius: '16px',
                                }}
                              >
                                <MenuItem value=""><em>field</em></MenuItem>
                                {newConditionData.contextType === 'account' &&
                                  Object.keys(FIELD_REGISTRY.account.fields).map((field) => (
                                    <MenuItem key={field} value={field}>{field}</MenuItem>
                                  ))}
                                {newConditionData.contextType === 'metadata' &&
                                  Object.keys(FIELD_REGISTRY.metadata.fields).map((field) => (
                                    <MenuItem key={field} value={field}>{field}</MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          )}

                          {/* Dot separator for entity fields */}
                          {newConditionData.contextType === 'entity' && newConditionData.field1 && (
                            <Typography variant="body2" sx={{ color: '#999', mx: -0.3 }}>.</Typography>
                          )}

                          {/* Field2: entity field (count, last7Days, etc.) */}
                          {newConditionData.contextType === 'entity' && newConditionData.field1 && (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={newConditionData.field2}
                                onChange={(e) => setNewConditionData({ ...newConditionData, field2: e.target.value, operator: '', value: '' })}
                                displayEmpty
                                sx={{
                                  bgcolor: '#b8e6d5',
                                  color: '#1B0826',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  height: '26px',
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                                  borderRadius: '16px',
                                }}
                              >
                                <MenuItem value=""><em>field</em></MenuItem>
                                {Object.keys(FIELD_REGISTRY.entity.fields).map((field) => (
                                  <MenuItem key={field} value={field}>{field}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {/* Operator */}
                          {newConditionData.field1 && (newConditionData.contextType !== 'entity' || newConditionData.field2) && (
                            <OperatorSelect
                              contextType={newConditionData.contextType}
                              field1={newConditionData.field1}
                              field2={newConditionData.field2}
                              value={newConditionData.operator}
                              onChange={(op) => setNewConditionData({ ...newConditionData, operator: op, value: '' })}
                              getFieldType={getFieldType}
                            />
                          )}

                          {/* Value */}
                          {newConditionData.operator && (
                            <ValueInput
                              contextType={newConditionData.contextType}
                              field1={newConditionData.field1}
                              field2={newConditionData.field2}
                              operator={newConditionData.operator}
                              value={newConditionData.value}
                              onChange={(val) => setNewConditionData({ ...newConditionData, value: val })}
                              getFieldType={getFieldType}
                            />
                          )}

                          {/* Action buttons */}
                          <Box display="flex" gap={0.5} ml="auto">
                            <IconButton size="small" onClick={() => setAddingCondition(null)} sx={{ width: 24, height: 24 }}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleSaveNewCondition}
                              disabled={!newConditionData.contextType || !newConditionData.field1 || !newConditionData.operator || !newConditionData.value}
                              sx={{
                                width: 24,
                                height: 24,
                                color: '#1B0826',
                                '&:hover': { bgcolor: 'rgba(27, 8, 38, 0.04)' },
                                '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' }
                              }}
                            >
                              <CheckIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Paper>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddConditionClick(stage.name)}
                          fullWidth
                          variant="outlined"
                          sx={{
                            color: '#1B0826',
                            borderColor: '#1B0826',
                            '&:hover': {
                              borderColor: '#1B0826',
                              bgcolor: 'rgba(27, 8, 38, 0.04)',
                            },
                          }}
                        >
                          Add Condition
                        </Button>
                      )}
                    </>
                  )}
                </Box>

                {stage.type === 'custom' && (
                  <Box mt={2} textAlign="center">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeleteStage(stage.name)}
                      startIcon={<DeleteIcon />}
                    >
                      Delete Stage
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* Arrow connector line */}
              {index < stages.length - 1 && (
                <Box
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 2,
                    bgcolor: '#d0d0d0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Arrow head */}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -6,
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid #d0d0d0',
                      borderTop: '4px solid transparent',
                      borderBottom: '4px solid transparent',
                    }}
                  />

                  {/* Add stage button */}
                  {canAddStageBetween(index) && addingBetween !== index ? (
                    <Box
                      onClick={() => handleAddStageBetween(index)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#DCF995',
                        border: '2px solid #DCF995',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#1B0826',
                          borderColor: '#1B0826',
                          transform: 'scale(1.1)',
                          '& .add-icon': {
                            color: '#DCF995',
                          },
                        },
                      }}
                    >
                      <AddIcon className="add-icon" sx={{ fontSize: 20, color: '#1B0826', transition: 'color 0.2s' }} />
                    </Box>
                  ) : !canAddStageBetween(index) && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#e0e0e0',
                        border: '2px solid #d0d0d0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: 16, color: '#999' }}>
                        ×
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* New Stage Card (inline) */}
              {addingBetween === index && newStage && (
                <>
                  {/* Left connector with arrow + add button */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: 80,
                      height: 2,
                      bgcolor: '#d0d0d0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -6,
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid #d0d0d0',
                        borderTop: '4px solid transparent',
                        borderBottom: '4px solid transparent',
                      }}
                    />
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#DCF995',
                        border: '2px solid #DCF995',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AddIcon sx={{ fontSize: 20, color: '#1B0826' }} />
                    </Box>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      minWidth: 400,
                      maxWidth: 500,
                      p: 2,
                      border: '2px dashed #1976d2',
                      borderRadius: 2,
                      bgcolor: '#f0f7ff',
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      NEW STAGE
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      label="Stage Name (unique identifier)"
                      value={newStage.name || ''}
                      onChange={(e) => setNewStage({ ...newStage, name: e.target.value, displayName: e.target.value ? e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1).replace(/_/g, ' ') : '' })}
                      sx={{ mb: 1.5 }}
                      placeholder="e.g., first_project"
                      helperText="Lowercase with underscores. Display name is auto-generated."
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={newStage.description || ''}
                      onChange={(e) => setNewStage({ ...newStage, description: e.target.value })}
                      multiline
                      rows={2}
                      sx={{ mb: 1.5 }}
                      placeholder="e.g., Customer has created their first project"
                    />

                    <Box mb={1.5}>
                      <Typography variant="body2" fontWeight={600} mb={0.5}>
                        Match Logic
                      </Typography>
                      <RadioGroup
                        row
                        value={newStage.conditions && 'any' in (newStage.conditions || {}) ? 'any' : 'all'}
                        onChange={(e) => {
                          const logic = e.target.value as 'all' | 'any';
                          const conditions = newStage.conditions?.all || newStage.conditions?.any || [];
                          setNewStage({ ...newStage, conditions: { [logic]: conditions } });
                        }}
                      >
                        <FormControlLabel value="all" control={<Radio size="small" />} label="ALL conditions must match" />
                        <FormControlLabel value="any" control={<Radio size="small" />} label="ANY condition matches" />
                      </RadioGroup>
                    </Box>

                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      Stage saved without conditions is inactive (matches no accounts) until you add at least one.
                    </Typography>

                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={handleCancelNewStage}
                        fullWidth
                        sx={{
                          color: '#1B0826',
                          borderColor: '#1B0826',
                          '&:hover': {
                            borderColor: '#1B0826',
                            bgcolor: 'rgba(27, 8, 38, 0.04)',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        onClick={handleSaveNewStage}
                        fullWidth
                        disabled={!newStage.name}
                        sx={{
                          bgcolor: '#DCF995',
                          color: '#1B0826',
                          '&:hover': { bgcolor: '#c8e577' },
                          '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#999' },
                        }}
                      >
                        Save Stage
                      </Button>
                    </Box>
                  </Paper>
                  {/* Right connector with arrow */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: 80,
                      height: 2,
                      bgcolor: '#d0d0d0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -6,
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid #d0d0d0',
                        borderTop: '4px solid transparent',
                        borderBottom: '4px solid transparent',
                      }}
                    />
                  </Box>
                </>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

    </Box>
  );
};
